import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

import { SelectMulti } from 'twyne-new.ui';

import Input from '@Components/formsy/Input';
import MaskedInput from '@Components/formsy/MaskedInput';
import Select from '@Components/formsy/Select';
import { COUNTRIES } from '@Modules/settings/views/blacklist/components/Form';
import { ACTION_OPTIONS } from '@Modules/settings/views/integration/components/utils';
import { useGlobalStore } from '@Stores/global';

import { genderOptions, booleanOptions, weekdayOptions, fraudOptions, getAllocationOptions, getRevenueIntegrationOptions, getPublisherOptions, getOfferOptions, getFormOptions, getPageOptions, getPathUserProfileOptions } from './options'

function RuleItemInput({ type, value, onChange, disabled, name = "value-rule" }) {
  const handleChangeInput = e => {
    onChange(e);
  }

  const handleChangeSelect = opt => {
    const value = opt ? opt.value : null;
    onChange(value);
  }

  const handleChangeSelectMulti = opt => {
    const value = opt ? opt.map(item => item.value).join(',') : null
    onChange(value)
  }

  const props = {
    value,
    onChange: handleChangeSelect,
    required: true,
    disabled,
    clearable: false,
    name,
  }

  if (type === 'usr_country') {
    return <Select options={COUNTRIES} {...props} />
  }

  if (type === 'gender') {
    return <Select options={genderOptions} {...props} />
  }

  if (type === 'fraud-score') {
    return <Select options={fraudOptions} {...props} />
  }

  if (type === 'allocation') {
    return <Select async options={optionalize(getAllocationOptions)} {...props} key='allocation' />
  }

  if (type === 'integration-revenue') {
    return <Select async options={optionalize(getRevenueIntegrationOptions)} {...props} key='integration-revenue' />
  }

  if (type === 'publisher') {
    return <Select async options={optionalize(getPublisherOptions)} {...props} key='publisher' />
  }

  if (type === 'offer') {
    return <Select async options={optionalize(getOfferOptions)} {...props} key='offer' />
  }

  if (type === 'form') {
    return <Select async options={optionalize(getFormOptions)} {...props} key='form' />
  }

  if (type === 'page') {
    return <Select async options={optionalize(getPageOptions)} {...props} key='page_id' />
  }

  if (type === 'pathUserProfile') {
    return <Select async options={optionalize(getPathUserProfileOptions)} {...props} key='path_user_profile_id' />
  }

  if (type === 'action') {
    return <Select options={ACTION_OPTIONS} {...props} />
  }

  if (type === 'boolean') {
    return <Select options={booleanOptions} {...props} />
  }

  if (type === 'weekday') {
    return <Select options={weekdayOptions} {...props} />
  }

  if (type === 'tcpaConsentDba') {
    const value = props.value ? props.value.split(',') : []
    return <TcpaConsentDbaSelectCreatable {...props} value={value} onChange={handleChangeSelectMulti} />
  }

  if (type === 'date' || type === 'datetime') {
    return <Input name={name} type='date' value={value} disabled={disabled} onChange={onChange} required />
  }

  if (type === 'time') {
    return <MaskedInput
      name={name}
      className="form-control"
      mask={[/[0-2]/, /[0-9]/, ':', /[0-5]/, /[0-9]/]}
      value={value}
      onChange={handleChangeInput}
      disabled={disabled}
      required
    />
  }

  return <Input name={name} type={type === 'integer' || type === 'float' ? 'number' : 'text'} value={value}
    disabled={disabled}
    step={type === 'float' ? "0.01" : '1'}
    onChange={handleChangeInput} required />
}

const TcpaConsentDbaSelectCreatable = ({ value = [], onChange, ...props }) => {
  const { siteSettings: { useSystemConfig } } = useGlobalStore()
  const tcpaConsentDba = useSystemConfig('midpath.tcpa-consent-dba')

  const [options, setOptions] = useState([])

  useEffect(() => {
    if (options.length) {
      return
    }

    const dbasOptions = [...tcpaConsentDba || []]
    const dbasDefault = dbasOptions.map(item => item.value)
    const dbasCustom = value.filter(item => !dbasDefault.includes(item))
    dbasOptions.push(...dbasCustom.map(item => ({ label: item, value: item })))
    setOptions(dbasOptions)
  }, [tcpaConsentDba, value, options])

  const onChangeItems = items => {
    if (items.length > options.length) {
      setOptions(items)
    }
    onChange(items)
  }

  return <>
    <SelectMulti
      {...props}
      required
      creatable
      value={value}
      option={options}
      options={options}
      onChange={items => items && onChangeItems(items)}
      isValidNewOption={label => typeof label === 'string' && label.length && !/[^\w]/.test(label)}
      promptTextCreator={str => `Create a DBA named "${str}"`}
      newOptionCreator={({ label }) => ({ label, value: label })}
      placeholder={!value.length ? 'Type to create an option' : 'Select...'}
    />
  </>
}

RuleItemInput.propTypes = {
  type: PropTypes.string,
  value: PropTypes.any,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  name: PropTypes.string,
};

export default RuleItemInput;

const optionalize = fn => () => fn().then(options => ({ options }))
